import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GraduationCap,
  ShieldCheck,
  Scale,
  Users,
  Briefcase,
  FileSignature,
  CheckCircle2,
  ArrowRight,
  Building2,
  Mail,
  Phone,
} from "lucide-react";
import { site } from "@/lib/site";

export const Route = createFileRoute("/corporate-training")({
  head: () => ({
    meta: [
      { title: "Corporate Training — Labour Law, POPIA, HR Compliance | Nkadimeng Danny Legal" },
      {
        name: "description",
        content:
          "On-site and virtual corporate training across South Africa: Labour Law, POPIA, workplace misconduct, chairperson training, HR compliance and CCMA preparation. Practical workshops for SMEs, HR teams and managers.",
      },
      { property: "og:title", content: "Corporate Training — Nkadimeng Danny Legal" },
      {
        property: "og:description",
        content:
          "Practical labour-law, POPIA, HR-compliance and chairperson workshops for South African businesses.",
      },
      { property: "og:url", content: "/corporate-training" },
    ],
    links: [{ rel: "canonical", href: "/corporate-training" }],
  }),
  component: CorporateTrainingPage,
});

const programmes = [
  {
    icon: Scale,
    title: "Labour Law Essentials",
    duration: "Half-day · ½D",
    audience: "Managers, supervisors, business owners",
    body: "BCEA, LRA basics, fair process, contracts of employment, leave, overtime, termination categories and notice. Practical scenarios drawn from real cases.",
  },
  {
    icon: ShieldCheck,
    title: "POPIA for the Workplace",
    duration: "Half-day · ½D",
    audience: "All employees handling client or employee data",
    body: "Lawful processing, consent, operator agreements, breach response, data-subject rights and a practical compliance baseline for SMEs.",
  },
  {
    icon: Briefcase,
    title: "Workplace Misconduct Workshop",
    duration: "Full-day · 1D",
    audience: "HR, line managers, business owners",
    body: "Schedule 8 LRA, progressive discipline, investigations, charge sheets, evidence handling and the difference between misconduct and incapacity.",
  },
  {
    icon: Users,
    title: "Chairperson Training",
    duration: "Full-day · 1D",
    audience: "Internal chairpersons and HR practitioners",
    body: "Conducting a fair disciplinary hearing, rulings on procedural objections, weighing evidence, drafting reasoned findings and sanction.",
  },
  {
    icon: FileSignature,
    title: "HR Compliance Bootcamp",
    duration: "Two-day · 2D",
    audience: "HR teams in SMEs and growing businesses",
    body: "End-to-end compliance: contracts, policies, BCEA, EEA basics, POPIA, grievance handling, CCMA referrals and record-keeping.",
  },
  {
    icon: GraduationCap,
    title: "CCMA Preparation Clinic",
    duration: "Half-day · ½D",
    audience: "Employers facing referrals; HR teams",
    body: "LRA Form 7.11, jurisdiction, con-arb, bundle preparation, witness statements, opening and closing arguments, and reviewable awards.",
  },
] as const;

function CorporateTrainingPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-24">
          <span className="gold-rule" />
          <p className="text-xs uppercase tracking-[0.25em] text-gold">
            Corporate training
          </p>
          <h1 className="mt-2 font-display text-4xl text-navy md:text-5xl">
            Practical legal &amp; compliance training for South African workplaces
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            On-site, virtual or hybrid workshops built around your team's real
            scenarios. Plain-language delivery, working templates and a
            certificate of attendance for every participant.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={`mailto:${site.email}?subject=${encodeURIComponent("Corporate training enquiry")}`}
              className="inline-flex items-center gap-2 rounded-md bg-navy px-5 py-3 text-sm font-semibold text-navy-foreground hover:bg-navy/90"
            >
              Request a quote <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-5 py-3 text-sm font-medium text-foreground hover:border-navy"
            >
              Book a scoping call
            </Link>
          </div>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {programmes.map((p) => (
            <article
              key={p.title}
              className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft"
            >
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-gold">
                  <p.icon className="h-5 w-5" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold">
                  {p.duration}
                </span>
              </div>
              <h2 className="mt-4 font-display text-lg text-navy">{p.title}</h2>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                {p.audience}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85">
                {p.body}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40 py-16">
        <div className="container-narrow grid gap-10 md:grid-cols-[1fr_1fr]">
          <div>
            <span className="gold-rule" />
            <h2 className="font-display text-3xl text-navy md:text-4xl">
              What's included
            </h2>
            <ul className="mt-6 space-y-3 text-sm text-foreground/85">
              {[
                "Custom agenda built from your team's real cases and policies",
                "Plain-language workbook and slide pack for every participant",
                "Working templates: charge sheets, policies, consent clauses, CCMA bundle index",
                "Live Q&A and scenario-based exercises",
                "Certificate of attendance for each delegate",
                "30-day follow-up email window for clarification questions",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-gold/40 bg-card p-6 shadow-lifted md:p-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-gold">
              <Building2 className="h-4 w-4" /> Delivery
            </div>
            <h3 className="mt-2 font-display text-2xl text-navy">
              On-site, virtual or hybrid
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              National coverage. In-person delivery across Gauteng and Limpopo,
              virtual via Microsoft Teams or Zoom anywhere in South Africa.
            </p>
            <div className="mt-5 space-y-3 text-sm">
              <a
                href={`mailto:${site.email}?subject=${encodeURIComponent("Corporate training enquiry")}`}
                className="flex items-center gap-3 rounded-md border border-input bg-background px-4 py-3 hover:border-navy"
              >
                <Mail className="h-4 w-4 text-navy" />
                {site.email}
              </a>
              <a
                href={`tel:${site.phoneIntl}`}
                className="flex items-center gap-3 rounded-md border border-input bg-background px-4 py-3 hover:border-navy"
              >
                <Phone className="h-4 w-4 text-navy" />
                {site.phone}
              </a>
            </div>
            <p className="mt-5 text-xs text-muted-foreground">
              Group rates and recurring quarterly programmes available.
            </p>
          </div>
        </div>
      </section>

      <section className="container-narrow py-16">
        <div className="rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] text-gold">
            Scope reminder
          </p>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            Training is educational and practical. It is not legal advice, and
            attending a workshop does not create an attorney-client
            relationship. Where a specific matter requires representation, we
            refer to an admitted attorney.
          </p>
        </div>
      </section>
    </>
  );
}
